// 콤보관리(SY12) 뷰 — 디자인 핸드오프 없이 메뉴관리(sy11) 뷰(`menuManagement/MenuManagementView.tsx`)를 복사해 고친 손코드(LOGISPRD-16045).
//  같은 core.* 요소·화면 순서(제목 → 조회 카드 → 표 묶음)를 쓰고, 화주 필터·엑셀은 뺐다(1.0 SY05 에 없음).
//  버튼 권한·화면관리 그리드 설정은 공통코드(sy10) 생성본의 permCan·permTool·permCols 와 같은 모양으로 붙였다.
//  배선(Wiring)은 화면 엔트리(index.tsx)가 props 로 넘긴다.
import { core } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'
import '@musinsa/pbo-core/tokens.css'
import '@musinsa/pbo-core/styles.css'
import '@musinsa/pbo-core/fonts.css'
import './screenShell.css'
import { type ComponentProps, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { Icon as DsIcon } from '@musinsa/pbo-assets'
import { usePageHeaderSticky } from '@/components/pbo/pageHeaderSticky'
import type { usePermissionSlot } from '@/hooks/useMenuPermission'
import type { ButtonCode } from '@/services/auth/menu.types'
import { SY12_PAGE_SIZE, TABLE_ID } from './consts'

// 모듈 홀더: 확인창 함수·선택 게이트 등 모듈 수준 코드가 같은 배선을 보도록 렌더 첫머리에서 갱신한다. props.wiring 은 렌더마다 새 객체여도 된다(효과 의존성에 쓰지 않는다).
let wiring: Wiring = { rows: {}, actions: {} } as Wiring

export type Row = Record<string, unknown>
/** 액션 슬롯 인자 — selected = 표의 체크 선택 행 · visible = 목록 검색 뒤 화면에 보이는 행 */
export type ActionCtx = { selected: Row[]; visible?: Row[] }
export type ColumnLayout = Pick<core.CoreTableProps,'columnOrder' | 'onColumnOrderChange' | 'columnWidths' | 'onColumnWidthsChange' | 'pinnedColumns' | 'onPinnedColumnsChange' | 'hiddenColumns' | 'onHiddenColumnsChange'>
export type Wiring = {
  /** 표 데이터 — 클라이언트 페이징. loading 이면 표 위에 로더를 띄운다. */
  rows: { ROWS: () => { rows: Row[]; loading?: boolean } }
  /** 동작 — 실패는 throw(확인창이 열린 채 남고 선택도 유지된다). 성공 토스트·닫기는 뷰가 한다. */
  actions: {
    search: (filters: Record<string, unknown>) => void | false | Promise<void | false>
    sy12AddButton: () => void
    ROWS_rowsChange: (rows: Record<string, unknown>[]) => void
    sy12SaveConfirm_ok: (ctx: ActionCtx) => void | Promise<void>
    sy12DeleteConfirm_ok: (ctx: ActionCtx) => void | Promise<void>
  }
  /** 요청 진행 중 여부(React 훅) — true 면 선택 게이트 버튼(저장·삭제)이 잠긴다. */
  useBusy?: () => boolean
  /** [초기화] 때 — 조회 결과도 지운다. 뷰는 조회영역 값·페이저·선택·목록 검색어를 되돌린다. */
  onReset?: () => void
  columnLayout?: { [TABLE_ID]?: (columns: core.CoreColumnType[]) => ColumnLayout }
  /** 버튼 권한·화면관리 그리드 설정(usePermissionSlot 값). 없으면 제한하지 않는다. */
  permission?: ReturnType<typeof usePermissionSlot>
}

const permCan = (code: ButtonCode) => wiring.permission?.canShow(code) ?? true
const permTable = () => wiring.permission?.forTable(TABLE_ID)
/** 툴바 — 화면관리 SY12_HEAD TOOL_* 와 버튼 레벨을 AND 한 값 */
const permAdd = () => permTable()?.toolbar.add ?? true
/** 컬럼 — 편집 잠금(레벨)과 필수(화면관리 설정)를 반영한다 */
const permCols = (columns: core.CoreColumnType[]) => permTable()?.columns(columns) ?? columns

const FILTER_DEFAULTS: Record<string, unknown> = {}

/** 컬럼 키 = 1.0 COLUMN_CD(화면관리 SY12_HEAD 컬럼 코드). 기존 행도 4칸 모두 고칠 수 있다(1.0 SY05 동일). */
const COLUMNS: core.CoreColumnType[] = [
  { title: '콤보코드', width: 160, variant: 'input', required: true, dataIndex: 'COMBO_CD' },
  { title: '콤보명', width: 220, variant: 'input', dataIndex: 'COMBO_NM' },
  { title: '옵션코드', width: 160, variant: 'input', required: true, dataIndex: 'COMBO_OPT_CD' },
  { title: '옵션명', width: 220, variant: 'input', dataIndex: 'COMBO_OPT_NM' },
  { title: '생성일자', width: 150, dataIndex: 'INS_DATE' },
  { title: '생성자', width: 160, dataIndex: 'INS_BY' },
  { title: '수정일자', width: 150, dataIndex: 'UPD_DATE' },
  { title: '수정자', width: 160, dataIndex: 'UPD_BY' },
]

// 걸러진 행 브리지 — 액션 슬롯 인자(ctx.visible)
const visRef: { current: Record<string, unknown>[] } = { current: [] }
const confirmSy12Save = () =>
  core.Modal.confirm({
    icon: <span style={{ display: 'inline-flex', lineHeight: 0, marginInlineEnd: 12 }}><DsIcon name="ic-alertCircle" size={22} color="#faad14" filled /></span>,
    title: '알림',
    content: fillTok('선택한 {count}건의 콤보를 저장하시겠어요?'),
    okText: '저장',
    cancelText: '취소',
    onOk: async () => {
      const okMsg = fillTok('{count}건 저장 완료')
      await wiring.actions.sy12SaveConfirm_ok(selCtx())
      core.message.success(okMsg)
      selReset.current()
    },
  })
const confirmSy12Delete = () =>
  core.Modal.confirm({
    icon: <span style={{ display: 'inline-flex', lineHeight: 0, marginInlineEnd: 12 }}><DsIcon name="ic-alertCircle" size={22} color="#faad14" filled /></span>,
    title: '알림',
    // MOMS_COMBO_INFO 는 1.0·2.0 화면 드롭다운이 옵션으로 읽는다(GET /api/v1/combos) — 지우면 그 선택지가 함께 사라진다는 것을 알린다.
    content: <>{fillTok('선택한 {count}건의 콤보를 삭제하시겠어요?')}<br />삭제하면 이 옵션을 쓰는 모든 화면의 드롭다운에서 빠집니다.</>,
    okText: '삭제',
    cancelText: '취소',
    okButtonProps: { danger: true },
    onOk: async () => {
      const okMsg = fillTok('{count}건 삭제 완료')
      await wiring.actions.sy12DeleteConfirm_ok(selCtx())
      core.message.success(okMsg)
      selReset.current()
    },
  })
// 선택 행 브리지 — 액션 슬롯 인자(ctx.selected). 표의 체크 선택 행(SelBridge 가 채운다).
const selRef: { current: Record<string, unknown>[] } = { current: [] }
// 확인창(모듈 함수) 성공 뒤 표 선택 비우기 — 컴포넌트가 마운트 동안 setter 를 꽂는다
const selReset: { current: () => void } = { current: () => {} }
const selCtx = (): ActionCtx => ({ selected: selRef.current, visible: visRef.current })
// 문구 토큰 — {count} = 표 선택 수(호출 시점)
const fillTok = (s: string): string => s.replace(/\{count\}/g, () => String(selRef.current.length))

const filterFieldClass = (key: string): string => `pbo-ff--${key.replace(/[^A-Za-z0-9_-]/g, '_')}`
const FILTER_FOCUSABLE = 'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), [contenteditable=true], button:not([disabled])'
const FILTER_OPEN_EVENTS = ['pointerdown', 'mousedown', 'mouseup', 'click'] as const
const openFilterField = (open: () => void, key: string) => {
  open()
  window.setTimeout(() => {
    const frame = document.querySelector(`.${filterFieldClass(key)}`)
    if (!frame) return
    frame.querySelector<HTMLElement>(FILTER_FOCUSABLE)?.focus()
    const host = frame.querySelector<HTMLElement>('.ant-select-selector') ?? frame.querySelector<HTMLElement>('.ant-picker input')
    if (!host) return
    FILTER_OPEN_EVENTS.forEach((type) => host.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window })))
  }, 0)
}

const chipText = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null)

/** 조회영역 필드 프레임 — 접힘이면 값 칩(값 없으면 숨김), 열림이면 DS FormItem(라벨 위). 규격 = pbo-core filter-layout.css .pbo-filter-field */
function FilterField({ collapsed, label, required, width, chip, fieldKey, onOpen, children }: { collapsed: boolean; label?: string; required?: boolean; width?: number | string; chip?: string | null; fieldKey?: string; onOpen?: () => void; children?: ReactNode }) {
  if (collapsed) {
    const open = fieldKey && onOpen ? () => openFilterField(onOpen, fieldKey) : undefined
    return (
      <div
        className="pbo-filter-closed"
        hidden={chip == null}
        {...(open
          ? {
              role: 'button',
              tabIndex: 0,
              style: { cursor: 'pointer' },
              onClick: open,
              onKeyDown: (e: { key: string; preventDefault: () => void }) => {
                if (e.key !== 'Enter' && e.key !== ' ') return
                e.preventDefault()
                open()
              },
            }
          : {})}
      >
        {label ? (
          <div className="pbo-filter-chip-label">
            <label>{label}</label>
            {required ? <span className="pbo-filter-req">*</span> : null}
          </div>
        ) : null}
        <div className="pbo-filter-chip-value">{chip}</div>
      </div>
    )
  }
  return (
    <core.FormItem className={fieldKey ? `pbo-filter-field ${filterFieldClass(fieldKey)}` : 'pbo-filter-field'} label={label} required={required} labelPosition="top" boxedItem={false} style={width != null ? { width } : undefined}>
      {children}
    </core.FormItem>
  )
}

const noBusy = (): boolean => false
/** 선택 0개면 비활성 — 같은 core.TableSelectionScope 안의 표 선택 개수를 읽는다. 요청 중(useBusy)에도 잠긴다. */
function SelGatedButton({ disabled, ...rest }: ComponentProps<typeof core.Button>) {
  const n = core.useTableSelectionCount()
  const busy = (wiring.useBusy ?? noBusy)()
  return <core.Button {...rest} disabled={disabled || (n ?? 0) === 0 || busy} />
}

/**
 * 편집 시 자동 체크 표 — 셀을 고친 행을 체크 선택에 더한다(1.0 CommonUtil.onCellValueChanged 의 setSelected(true) 미러).
 * 생성본(commonCode/handoff/page.generated.tsx) EditSelTable 그대로.
 */
function EditSelTable<T extends Record<string, unknown>>({ rowSelection, onRowsChange, dataSource, ...rest }: Parameters<typeof core.Table<T>>[0]) {
  const [inner, setInner] = useState<React.Key[]>([])
  const keys = rowSelection?.selectedRowKeys ?? inner
  const cur = useRef(keys)
  cur.current = keys
  const last = useRef<T[] | null>(null)
  const keyField = typeof rest.rowKey === 'string' ? rest.rowKey : 'key'
  const keyOf = (r: T, i: number): React.Key => (r[keyField] as React.Key | undefined) ?? i
  const same = (a: T, b: T) => a === b || [...new Set([...Object.keys(a), ...Object.keys(b)])].every((k) => Object.is(a[k], b[k]))
  const edited = (next: T[]): React.Key[] => {
    const source = dataSource ?? []
    if (next.length !== source.length) return []
    const index = (rs: readonly T[]) => new Map(rs.map((r, i) => [String(keyOf(r, i)), r]))
    const src = index(source)
    const prev = last.current && last.current.length === next.length ? index(last.current) : null
    return next.flatMap((r, i) => {
      const k = keyOf(r, i)
      const a = src.get(String(k))
      if (!a || same(a, r)) return []
      const b = prev?.get(String(k))
      return b && same(b, r) ? [] : [k]
    })
  }
  return (
    <core.Table<T>
      {...rest}
      dataSource={dataSource}
      rowSelection={rowSelection && {
        ...rowSelection,
        selectedRowKeys: keys,
        onChange: (k, rows, info) => {
          setInner(k)
          cur.current = k
          rowSelection.onChange?.(k, rows, info)
        },
      }}
      onRowsChange={(next) => {
        const added = edited(next)
        last.current = next
        onRowsChange?.(next)
        if (!added.length) return
        // DS 표는 onRowsChange 를 자기 상태 갱신 함수 안에서 부른다 — 곧장 이 컴포넌트를 갱신하면 렌더 중 갱신 경고라 한 박자 미룬다.
        queueMicrotask(() => {
          const have = new Set(cur.current.map(String))
          const extra = added.filter((k) => !have.has(String(k)))
          if (!extra.length) return
          const merged = [...cur.current, ...extra]
          const set = new Set(merged.map(String))
          cur.current = merged
          setInner(merged)
          rowSelection?.onChange?.(merged, next.filter((r, i) => set.has(String(keyOf(r, i)))), { type: 'multiple' })
        })
      }}
    />
  )
}

/** 표 카운트 — '선택 N / 전체' */
function SelStat(props: ComponentProps<typeof core.TableStat>) {
  const n = core.useTableSelectionCount()
  return <core.TableStat {...props} selectedCount={n && n > 0 ? n : undefined} />
}

/** 선택 행 브리지 — 표의 선택 행을 모듈 변수 selRef 로 흘려 보낸다. */
function SelBridge() {
  const s = core.useTableSelection()
  const rows = s?.rows
  useEffect(() => {
    selRef.current = rows ?? []
    return () => {
      selRef.current = []
    }
  }, [rows])
  return null
}

export default function ComboManagementView(props: { wiring: Wiring }) {
  wiring = props.wiring // 모듈 홀더 갱신 — 렌더마다 같은 참조
  const header = usePageHeaderSticky()
  const [filters, setFilters] = useState<Record<string, unknown>>(FILTER_DEFAULTS)
  const [collapsed, setCollapsed] = useState(false)
  const [selEpoch, setSelEpoch] = useState(0)
  const [pager, setPager] = useState({ page: 1, size: SY12_PAGE_SIZE })
  const [listQ, setListQ] = useState('')
  const { rows: ROWS, loading } = wiring.rows.ROWS()
  const columns = permCols(COLUMNS)
  const layout = wiring.columnLayout?.[TABLE_ID]?.(columns)
  // [목록 검색] 표시 컬럼 전체 부분일치(대소문자 무시)
  const listed = useMemo(() => {
    const q = listQ.trim().toLowerCase()
    if (!q) return ROWS
    const idx = COLUMNS.map((c) => c.dataIndex).filter((d): d is string => typeof d === 'string')
    return ROWS.filter((x) => idx.some((di) => String((x[di] ?? '') as string | number).toLowerCase().includes(q)))
  }, [listQ, ROWS])
  visRef.current = listed
  const page = Math.min(pager.page, Math.max(1, Math.ceil(listed.length / pager.size)))
  const pageRows = listed.slice((page - 1) * pager.size, page * pager.size)
  useEffect(() => { document.body.classList.add('pbo-screen-portal'); return () => { document.body.classList.remove('pbo-screen-portal') } }, [])
  useEffect(() => { selReset.current = () => setSelEpoch((n) => n + 1); return () => { selReset.current = () => {} } }, [])
  return (
    <core.ConfigProvider theme={pboLogisticsTheme}>
      <div className="pbo-core-screen pbo-screen-shell pbo-fonts">
        <core.Flex vertical gap={16} align="stretch">
          <core.WorkspaceTab style={{ margin: '-24px -24px 0px -24px' }} activeLabel="콤보관리" />
          <core.Flex gap={8} justify="space-between" align="center" ref={header.ref} className={`dev-page-header-wrap pin${header.isSticky ? ' sticky' : ''}`} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', height: 'auto', minHeight: 40, top: 'calc(12px - var(--pbo-page-header-scroller-pad, 0px))', ['--pbo-page-header-bleed-top' as string]: '36px' }}>
            <core.Typography.Title level={4} style={{ margin: 0, whiteSpace: 'pre-line' }}>콤보관리</core.Typography.Title>
            <core.Flex gap={16} align="center">{null}</core.Flex>
          </core.Flex>
          <core.Card styles={{ body: { padding: '12px 20px' } }} style={{ border: '1px solid #d9d9d9', borderRadius: 8, minWidth: 0 }}>
            <div className={collapsed ? 'pbo-filter-collapse-closed' : 'pbo-filter-collapse-open'}>
              {collapsed && <span className="pbo-filter-empty-msg">필터가 적용된 항목이 없습니다. 필터를 열어 원하는 조건으로 검색하세요.</span>}
              <core.Flex gap={20} align="flex-start" wrap>
                <FilterField collapsed={collapsed} label="콤보코드" chip={chipText(filters.COMBO_CD)} fieldKey="sy12ComboCode" onOpen={() => setCollapsed(false)}>
                  <core.Input placeholder="콤보코드 입력" value={(filters.COMBO_CD as string | undefined) ?? ''} onChange={(e) => setFilters((s) => ({ ...s, COMBO_CD: e.target.value }))} style={{ width: '100%' }} />
                </FilterField>
                <FilterField collapsed={collapsed} label="콤보명" chip={chipText(filters.COMBO_NM)} fieldKey="sy12ComboName" onOpen={() => setCollapsed(false)}>
                  <core.Input placeholder="콤보명 입력" value={(filters.COMBO_NM as string | undefined) ?? ''} onChange={(e) => setFilters((s) => ({ ...s, COMBO_NM: e.target.value }))} style={{ width: '100%' }} />
                </FilterField>
              </core.Flex>
              <core.Flex gap={8} justify="flex-end" align="center">
                <core.Button icon={collapsed ? <DownOutlined /> : <UpOutlined />} onClick={() => setCollapsed((c) => !c)}>{`필터 ${collapsed ? '열기' : '닫기'}`}</core.Button>
                <span style={{ height: 18, margin: '0 0px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />
                {permCan('CLEAR') && <core.Button onClick={() => { setFilters(FILTER_DEFAULTS); setPager((s) => (s.page === 1 ? s : { ...s, page: 1 })); setSelEpoch((n) => n + 1); setListQ(''); wiring.onReset?.() }}>초기화</core.Button>}
                {permCan('SEARCH') && <core.Button type="primary" onClick={() => { Promise.resolve(wiring.actions.search(filters)).then((r) => { if (r === false) return; setPager((s) => (s.page === 1 ? s : { ...s, page: 1 })); setSelEpoch((n) => n + 1); setListQ('') }, () => undefined) }}>조회</core.Button>}
              </core.Flex>
            </div>
          </core.Card>
          <core.TableSelectionScope key={selEpoch}><SelBridge />
            <core.Flex vertical gap={8} align="stretch">
              <core.Flex style={{ padding: '4px 0px 4px 0px' }} gap={8} justify="space-between" align="center" className="pbo-v2-actionbar-sticky">
                <core.Space>
                  <SelStat total={ROWS.length} />
                  {permCan('SAVE') && <SelGatedButton type="primary" onClick={confirmSy12Save}>저장</SelGatedButton>}
                  {permCan('DELETE') && <SelGatedButton onClick={confirmSy12Delete}>삭제</SelGatedButton>}
                </core.Space>
                <core.Space>
                  <core.TablePager variant="paged" page={page} pageSize={pager.size} total={listQ.trim() ? listed.length : ROWS.length} showSizeChanger={false} onPage={(p, size) => { setSelEpoch((n) => n + 1); setListQ(''); setPager({ page: size !== pager.size ? 1 : p, size }) }} />
                  <core.Input.Search placeholder="목록 검색" allowClear key={selEpoch} style={{ width: 240 }} defaultValue={listQ} onSearch={(v) => setListQ((v ?? '').trim())} />
                  {permAdd() && <span style={{ height: 18, margin: '0 4px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />}
                  {permAdd() && <core.Button icon={<DsIcon name="ic-plus" size={16} color="currentColor" />} onClick={() => wiring.actions.sy12AddButton()}>추가</core.Button>}
                </core.Space>
              </core.Flex>
              <EditSelTable columns={columns} dataSource={pageRows} loading={loading} rowSelection={{ type: 'checkbox', columnWidth: 48, fixed: true }} stickyOffset={104} size="middle" scroll={{ x: 'max-content' }} onRowsChange={(next) => wiring.actions.ROWS_rowsChange(next as Record<string, unknown>[])} columnOrder={layout?.columnOrder} onColumnOrderChange={layout?.onColumnOrderChange} columnWidths={layout?.columnWidths} onColumnWidthsChange={layout?.onColumnWidthsChange} pinnedColumns={layout?.pinnedColumns} onPinnedColumnsChange={layout?.onPinnedColumnsChange} hiddenColumns={layout?.hiddenColumns} onHiddenColumnsChange={layout?.onHiddenColumnsChange} />
            </core.Flex>
          </core.TableSelectionScope>
        </core.Flex>
      </div>
    </core.ConfigProvider>
  )
}
